import styled, { css, keyframes } from "styled-components";
import COLORS from "shared/constants/colors";

const Page = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 420px);
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: calc(100vh - 120px);
  margin: -20px -20px 0;
  overflow-x: clip;

  @media (max-width: 1024px) {
    grid-template-columns: minmax(0, 1fr);
  }

  @media (max-width: 768px) {
    margin: 0;
    min-height: auto;
  }
`;

const MainColumn = styled.div`
  min-width: 0;
  width: 100%;
  padding: 32px 48px 48px;
  background: ${COLORS.WHITE};

  @media (max-width: 768px) {
    padding: 20px 16px 24px;
  }
`;

const SummaryColumn = styled.aside`
  min-width: 0;
  width: 100%;
  padding: 32px 24px 48px;
  background: #f7f8fa;
  border-left: 1px solid #e5e7eb;

  @media (max-width: 1024px) {
    border-left: none;
    border-top: 1px solid #e5e7eb;
    padding: 24px 16px 32px;
  }

  @media (max-width: 768px) {
    padding: 20px 12px 24px;
  }
`;

const StepsNav = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  font-family: "Inter400";

  @media (max-width: 480px) {
    font-size: 12px;
    gap: 6px;
  }
`;

const CheckoutTopBar = styled.div`
  margin-bottom: 28px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const ContinueShoppingWrap = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    margin-top: 16px;
    padding-top: 16px;
  }
`;

const StepLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: ${({ $active }) => ($active ? "#18191f" : "#9ca3af")};
  font-family: ${({ $active }) => ($active ? "Inter600" : "Inter400")};
  font-size: 13px;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  &:hover {
    color: ${({ $clickable }) => ($clickable ? COLORS.BLUE : undefined)};
  }
`;

const StepSeparator = styled.span`
  color: #d1d5db;
`;

const SectionTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 18px;
  font-family: "Inter600";
  color: #18191f;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 12px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;

  ${SectionTitle} {
    margin: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const SectionLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: ${COLORS.BLUE};
  font-size: 14px;
  font-family: "Inter400";
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const FormSection = styled.section`
  margin-bottom: 28px;
  min-width: 0;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $columns = 1 }) => {
    if ($columns === 3) return "1fr 1fr 1fr";
    if ($columns === 2) return "1fr 1fr";
    return "1fr";
  }};
  column-gap: 14px;
  row-gap: 20px;
  min-width: 0;

  @media (max-width: 768px) {
    grid-template-columns: ${({ $columns = 1 }) =>
      $columns > 1 ? "1fr 1fr" : "1fr"};
    column-gap: 12px;
    row-gap: 18px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FieldRow = styled.div`
  grid-column: 1 / -1;
`;

const FieldNote = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: #6b7280;
  font-family: "Inter400";
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin-top: 24px;
    padding-top: 20px;
  }
`;

const FooterActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-left: auto;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    margin-left: 0;
    gap: 10px;

    button {
      width: 100% !important;
    }
  }
`;

const ContinueButtonWrap = styled.div`
  button {
    min-width: 220px;
  }

  @media (max-width: 768px) {
    width: 100%;

    button {
      min-width: 0;
      width: 100%;
    }
  }
`;

const BackLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: ${COLORS.BLUE};
  font-size: 14px;
  font-family: "Inter400";
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SummaryItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const SummaryItemThumbWrap = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
`;

const QuantityBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #6b7280;
  color: ${COLORS.WHITE};
  font-size: 11px;
  font-family: "Inter600";
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const SummaryItemBody = styled.div`
  min-width: 0;
`;

const SummaryItemTitle = styled.p`
  margin: 0 0 8px;
  font-size: 14px;
  font-family: "Inter600";
  color: #18191f;
`;

const SummaryItemDetail = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
  background: ${COLORS.WHITE};
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const SummaryItemThumb = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  border-radius: 4px;
`;

const SummaryItemMeta = styled.div`
  min-width: 0;
`;

const SummaryItemFile = styled.p`
  margin: 0;
  font-size: 12px;
  color: #374151;
  font-family: "Inter500";
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 480px) {
    white-space: normal;
    word-break: break-word;
  }
`;

const SummaryItemSize = styled.p`
  margin: 2px 0 0;
  font-size: 12px;
  color: #6b7280;
  font-family: "Inter400";
`;

const SummaryItemOptions = styled.ul`
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SummaryItemOption = styled.li`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-family: "Inter500";
  color: #166534;

  svg {
    font-size: 14px;
    color: #22c55e;
  }
`;

const SummaryItemPrice = styled.span`
  font-size: 14px;
  font-family: "Inter600";
  color: #18191f;
  white-space: nowrap;

  @media (max-width: 480px) {
    text-align: right;
  }
`;

const TotalsBlock = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #d1d5db;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const rushPulse = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.45);
    border-color: #f59e0b;
  }
  50% {
    box-shadow: 0 0 0 6px rgba(245, 158, 11, 0);
    border-color: #fbbf24;
  }
`;

const rushShimmer = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
`;

const RushSameDayRow = styled.label`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  width: 100%;
  gap: 8px;
  padding: 8px 10px;
  border: 1.5px solid ${({ $checked }) => ($checked ? "#d97706" : "#f59e0b")};
  border-radius: 8px;
  background: ${({ $checked }) =>
    $checked
      ? "linear-gradient(90deg, #fffbeb 0%, #fef3c7 50%, #fffbeb 100%)"
      : "linear-gradient(90deg, #fffbeb, #fef9c3, #fffbeb, #fef9c3)"};
  background-size: ${({ $checked }) => ($checked ? "100% 100%" : "300% 100%")};
  cursor: pointer;
  font-size: 14px;
  color: #92400e;
  font-family: "Inter600";
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;
  ${({ $checked }) =>
    !$checked &&
    css`
      animation:
        ${rushPulse} 1.8s ease-in-out infinite,
        ${rushShimmer} 2.4s linear infinite;
    `}

  &:hover {
    transform: translateY(-1px);
    border-color: #d97706;
  }

  input {
    width: 16px;
    height: 16px;
    accent-color: #d97706;
    cursor: pointer;
    flex-shrink: 0;
    margin: 0;
  }
`;

const RushSameDayLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;

  svg {
    font-size: 18px;
    color: #d97706;
  }
`;

const RushSameDayHint = styled.span`
  font-size: 11px;
  color: #b45309;
  font-family: "Inter600";
  white-space: nowrap;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.18);
  letter-spacing: 0.02em;
`;

const TotalLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
  color: #374151;
  font-family: "Inter400";
  flex-wrap: wrap;
`;

const GrandTotalLine = styled(TotalLine)`
  margin-top: 6px;
  padding-top: 14px;
  border-top: 1px solid #d1d5db;
  font-size: 18px;
  font-family: "Inter600";
  color: #18191f;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ShippingOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${({ $selected }) => ($selected ? COLORS.BLUE : "#e5e7eb")};
  border-radius: 8px;
  background: ${({ $selected }) => ($selected ? "#f0f9ff" : COLORS.WHITE)};
  cursor: pointer;

  @media (max-width: 480px) {
    flex-wrap: wrap;
    padding: 14px;
  }
`;

const ShippingOptionBody = styled.div`
  flex: 1;
`;

const ShippingOptionTitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-family: "Inter600";
  color: #18191f;
`;

const ShippingOptionDescription = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
  font-family: "Inter400";
`;

const ShippingOptionPrice = styled.span`
  font-size: 14px;
  font-family: "Inter600";
  color: #18191f;
  white-space: nowrap;

  @media (max-width: 480px) {
    margin-left: auto;
  }
`;

const PaymentCard = styled.div`
  padding: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const PaymentText = styled.p`
  margin: 0 0 16px;
  font-size: 14px;
  color: #374151;
  font-family: "Inter400";
  line-height: 1.5;
`;

const StripeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  background: ${COLORS.WHITE};
  border: 1px solid #e5e7eb;
  font-size: 13px;
  color: #6b7280;
  font-family: "Inter400";
`;

const EmptyCheckout = styled.div`
  padding: 48px 24px;
  text-align: center;
  width: 100%;
  max-width: 100%;

  @media (max-width: 768px) {
    padding: 32px 16px;
  }
`;

const EmptyCheckoutText = styled.p`
  margin: 0 0 16px;
  color: #6b7280;
  font-size: 15px;
  font-family: "Inter400";
`;

const MinOrderAlert = styled.div`
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
  font-family: "Inter500";
  line-height: 1.4;
`;

export {
  Page,
  MainColumn,
  SummaryColumn,
  CheckoutTopBar,
  ContinueShoppingWrap,
  StepsNav,
  StepLink,
  StepSeparator,
  SectionTitle,
  SectionHeader,
  SectionLink,
  FormSection,
  FieldGrid,
  FieldRow,
  FieldNote,
  FooterActions,
  FooterActionGroup,
  ContinueButtonWrap,
  BackLink,
  SummaryList,
  SummaryItem,
  SummaryItemThumbWrap,
  QuantityBadge,
  SummaryItemBody,
  SummaryItemTitle,
  SummaryItemDetail,
  SummaryItemThumb,
  SummaryItemMeta,
  SummaryItemFile,
  SummaryItemSize,
  SummaryItemOptions,
  SummaryItemOption,
  SummaryItemPrice,
  TotalsBlock,
  RushSameDayRow,
  RushSameDayLabel,
  RushSameDayHint,
  TotalLine,
  GrandTotalLine,
  ShippingOption,
  ShippingOptionBody,
  ShippingOptionTitle,
  ShippingOptionDescription,
  ShippingOptionPrice,
  PaymentCard,
  PaymentText,
  StripeBadge,
  EmptyCheckout,
  EmptyCheckoutText,
  MinOrderAlert,
};
